/* LHC — generisches Dropdown (Trigger + eigenes Listbox-Panel) als Ersatz für
   <select>, dessen Optionsliste das Betriebssystem zeichnet und die sich deshalb
   nicht ins CI bringen lässt.

   Markup: <lhc-dropdown class="lhc-dd"> mit [data-dd-trigger] (Button),
   [data-dd-label] (Text im Button), [data-dd-panel] (role="listbox") und je Option
   ein [data-dd-option] (role="option", data-value).

   Bei Auswahl feuert ein bubbelndes "lhc-dropdown:change" mit { value, option }.
   Optionen werden bei jedem Zugriff frisch gelesen, damit auch zur Laufzeit
   eingefügte Optionen (Personenzahl in der Buchungsstrecke) funktionieren. */

class LhcDropdown extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-dd-trigger]');
    this.panel = this.querySelector('[data-dd-panel]');
    this.labelEl = this.querySelector('[data-dd-label]');
    if (!this.trigger || !this.panel) return;

    this.onDocClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };

    this.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggle();
    });

    this.trigger.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      if (this.panel.hidden) this.open();
      this.focusOption(this.activeIndex());
    });

    this.panel.addEventListener('click', (event) => {
      const option = event.target.closest('[data-dd-option]');
      if (!option || !this.contains(option)) return;
      event.stopPropagation();
      this.select(option);
      this.close();
      this.trigger.focus();
    });

    this.panel.addEventListener('keydown', (event) => {
      const options = this.options;
      const index = options.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.focusOption(Math.min(index + 1, options.length - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.focusOption(Math.max(index - 1, 0));
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.focusOption(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.focusOption(options.length - 1);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (index < 0) return;
        this.select(options[index]);
        this.close();
        this.trigger.focus();
      } else if (event.key === 'Escape' || event.key === 'Tab') {
        this.close();
        this.trigger.focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
  }

  get options() {
    return Array.from(this.querySelectorAll('[data-dd-option]'));
  }

  get selectedOption() {
    return this.options.find((option) => option.classList.contains('is-active')) || null;
  }

  get value() {
    return this.selectedOption?.dataset.value ?? '';
  }

  activeIndex() {
    const index = this.options.findIndex((option) => option.classList.contains('is-active'));
    return index < 0 ? 0 : index;
  }

  focusOption(index) {
    this.options[index]?.focus();
  }

  toggle() {
    if (this.panel.hidden) this.open();
    else this.close();
  }

  open() {
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    document.addEventListener('click', this.onDocClick);
  }

  close() {
    if (!this.panel || this.panel.hidden) return;
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    document.removeEventListener('click', this.onDocClick);
  }

  /** Markiert die Option, zieht das Trigger-Label nach und meldet die Auswahl. */
  select(option, { silent = false } = {}) {
    if (!option) return;
    this.options.forEach((entry) => {
      const active = entry === option;
      entry.classList.toggle('is-active', active);
      entry.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (this.labelEl) this.labelEl.textContent = option.textContent.trim();
    if (silent) return;
    this.dispatchEvent(
      new CustomEvent('lhc-dropdown:change', {
        bubbles: true,
        detail: { value: option.dataset.value, option },
      })
    );
  }

  /** Wählt anhand von data-value, ohne ein Change-Event zu feuern. */
  selectValue(value) {
    const option = this.options.find((entry) => entry.dataset.value === String(value));
    if (option) this.select(option, { silent: true });
  }
}

if (!customElements.get('lhc-dropdown')) {
  customElements.define('lhc-dropdown', LhcDropdown);
}
